// ============================================================
// ALBATROS — RestockView.tsx
// Reposicion de stock: pedir a un proveedor los libros que estan a 0
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { Truck, Search, Minus, Plus, BookOpen, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import {
  LowStockBook,
  Supplier,
  listLowStockBooks,
  listActiveSuppliers,
  createRestockOrder
} from '../../services/PurchasingService';
import { getWarehouseId } from '../../services/TerminalService';
import { Skeleton } from '../common/Skeleton';
import './RestockView.css';

interface RestockViewProps {
  showToast: (title: string, description: string, iconType?: 'success' | 'alert') => void;
}

const PAGE_SIZE = 25;
const STOCK_LEVELS = [
  { value: 0, label: 'Sin stock (0)' },
  { value: 2, label: 'Stock bajo (hasta 2)' },
  { value: 5, label: 'Stock bajo (hasta 5)' },
  { value: 10, label: 'Stock bajo (hasta 10)' }
];

interface Selected {
  book: LowStockBook;
  quantity: number;
}

export default function RestockView({ showToast }: RestockViewProps) {
  const [search, setSearch] = useState('');
  const [maxStock, setMaxStock] = useState(0);
  const [page, setPage] = useState(1);
  const [books, setBooks] = useState<LowStockBook[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Lo elegido sobrevive al cambio de pagina y de busqueda: se guarda el libro entero.
  const [selected, setSelected] = useState<Record<string, Selected>>({});

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [isSending, setIsSending] = useState(false);

  const hasWarehouse = getWarehouseId() !== '';
  const requestId = useRef(0);

  useEffect(() => {
    listActiveSuppliers()
      .then(setSuppliers)
      .catch(err => showToast('Proveedores', err?.message || 'No se pudieron cargar los proveedores.', 'alert'));
  }, []);

  useEffect(() => {
    const id = ++requestId.current;
    setIsLoading(true);
    setError(null);

    // Debounce de la busqueda: una consulta por pausa, no por tecla.
    const timer = setTimeout(() => {
      listLowStockBooks({ search: search.trim(), maxStock, page, pageSize: PAGE_SIZE })
        .then(result => {
          if (id !== requestId.current) return;
          setBooks(result.books);
          setTotal(result.total);
          setIsLoading(false);
        })
        .catch(err => {
          if (id !== requestId.current) return;
          setBooks([]);
          setTotal(0);
          setError(err?.message || 'No se pudo cargar el listado.');
          setIsLoading(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [search, maxStock, page, reloadKey]);

  const changeQuantity = (book: LowStockBook, delta: number) => {
    setSelected(prev => {
      const current = prev[book.id] ? prev[book.id].quantity : 0;
      const next = Math.max(0, current + delta);
      const copy = { ...prev };
      if (next === 0) delete copy[book.id];
      else copy[book.id] = { book, quantity: next };
      return copy;
    });
  };

  const selectedList = Object.keys(selected).map(id => selected[id]);
  const totalUnits = selectedList.reduce((sum, s) => sum + s.quantity, 0);
  const canSend = hasWarehouse && !isSending && supplierId !== '' && selectedList.length > 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const order = await createRestockOrder(
        supplierId,
        selectedList.map(s => ({ bookId: s.book.id, title: s.book.title, quantity: s.quantity }))
      );
      const supplierName = suppliers.find(s => s.id === supplierId)?.name || 'el proveedor';
      showToast('Pedido enviado', `Orden ${order.number} para ${supplierName} enviada a aprobación.`, 'success');
      setSelected({});
      setReloadKey(k => k + 1); // los libros pedidos pasan a mostrar sus unidades "en pedido"
    } catch (err) {
      showToast('No se pudo crear el pedido', (err as any)?.message || 'Error desconocido', 'alert');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="rs-root al-scrollbar-hide">

      <div className="rs-header">
        <div className="rs-header-left">
          <div className="rs-header-icon"><Truck style={{ width: '1.5rem', height: '1.5rem' }} /></div>
          <div>
            <h2 className="rs-title">Reponer Stock</h2>
            <p className="rs-subtitle">Pide a un proveedor los libros que se han quedado sin existencias</p>
          </div>
        </div>
      </div>

      {!hasWarehouse && (
        <div className="rs-warning">
          Esta caja no tiene almacén asignado. Puedes ver el listado, pero no crear pedidos.
        </div>
      )}

      <div className="rs-filters">
        <div className="rs-search">
          <Search style={{ width: '1.25rem', height: '1.25rem' }} className="rs-search-icon" />
          <input
            type="text"
            placeholder="Buscar por título, autor o ISBN"
            value={search}
            onChange={e => { setSearch(e.target.value.replace(/[^A-Za-z0-9 ._\-ÁÉÍÓÚÜÑáéíóúüñ]/g, '')); setPage(1); }}
            className="rs-search-input"
          />
        </div>
        <select
          value={maxStock}
          onChange={e => { setMaxStock(Number(e.target.value)); setPage(1); }}
          className="rs-select"
        >
          {STOCK_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="rs-list">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`sk-${i}`} className="rs-row" style={{ opacity: 0.7 }}>
              <Skeleton width="2.5rem" height="2.5rem" radius="0.75rem" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                <Skeleton width="14rem" height="1rem" />
                <Skeleton width="9rem" height="0.875rem" />
              </div>
              <Skeleton width="7rem" height="2.5rem" radius="0.75rem" />
            </div>
          ))
        ) : error ? (
          <div className="rs-empty">{error}</div>
        ) : books.length === 0 ? (
          <div className="rs-empty">
            {search ? 'Ningún libro coincide con la búsqueda.' : 'No hay libros por debajo de ese nivel de stock.'}
          </div>
        ) : (
          books.map(book => {
            const quantity = selected[book.id] ? selected[book.id].quantity : 0;
            return (
              <div key={book.id} className={`rs-row${quantity > 0 ? ' rs-row--selected' : ''}`}>
                <div className="rs-thumb"><BookOpen style={{ width: '1.25rem', height: '1.25rem' }} /></div>
                <div className="rs-info">
                  <h4 className="rs-book-title">{book.title}</h4>
                  <p className="rs-book-meta">
                    {book.author || 'Sin autor'}{book.isbn ? ` • ${book.isbn}` : ''}
                  </p>
                </div>
                <div className="rs-badges">
                  <span className={`rs-stock${book.stock < 0 ? ' rs-stock--negative' : ''}`}>Stock {book.stock}</span>
                  {book.onOrder > 0 && (
                    <span className="rs-on-order" title="Unidades ya pedidas a proveedores y aún no recibidas">
                      Ya pedidas {book.onOrder}
                    </span>
                  )}
                </div>
                <div className="rs-qty">
                  <button onClick={() => changeQuantity(book, -1)} disabled={quantity === 0} className="rs-qty-btn" aria-label="Quitar una unidad">
                    <Minus style={{ width: '1rem', height: '1rem' }} />
                  </button>
                  <span className="rs-qty-value">{quantity}</span>
                  <button onClick={() => changeQuantity(book, 1)} className="rs-qty-btn rs-qty-btn--plus" aria-label="Añadir una unidad">
                    <Plus style={{ width: '1rem', height: '1rem' }} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="rs-pager">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rs-pager-btn">
            <ChevronLeft style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
          <span className="rs-pager-text">Página {page} de {lastPage} • {total} libros</span>
          <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage} className="rs-pager-btn">
            <ChevronRight style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>
      )}

      <div className="rs-footer">
        <div className="rs-footer-summary">
          <span className="rs-footer-count">{selectedList.length} {selectedList.length === 1 ? 'libro' : 'libros'}</span>
          <span className="rs-footer-units">{totalUnits} {totalUnits === 1 ? 'unidad' : 'unidades'}</span>
        </div>
        <select value={supplierId} onChange={e => setSupplierId(e.target.value)} className="rs-select rs-select--supplier">
          <option value="">Elige un proveedor…</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={handleSend} disabled={!canSend} className="rs-send">
          <Send style={{ width: '1.25rem', height: '1.25rem' }} />
          {isSending ? 'Enviando…' : 'Enviar pedido a aprobación'}
        </button>
      </div>

    </div>
  );
}
